import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

/**
 * CareBridgeAvatar Component
 * 3D Animated Avatar using Three.js and CareBridge.glb
 * Uses parent Pivot Group for 100% dead-center geometric alignment
 * and persistent WebGL canvas for rock-solid, jump-free rendering.
 */
const CareBridgeAvatar = ({
  visemeId = 0,
  isSpeaking = false,
  isWaving = false,
  width = '100%',
  height = '320px',
  className = '',
}) => {
  const mountRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // References for Three.js objects
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const mixerRef = useRef(null);
  const modelRef = useRef(null);
  const mouthMeshRef = useRef(null);
  const targetMorphsRef = useRef({ mouthOpen: 0, mouthRound: 0, mouthWide: 0 });
  const currentMorphsRef = useRef({ mouthOpen: 0, mouthRound: 0, mouthWide: 0 });
  const isSpeakingRef = useRef(isSpeaking);
  const actionsRef = useRef({});
  const animationFrameIdRef = useRef(null);
  const modelBoundsRef = useRef({ size: new THREE.Vector3(), center: new THREE.Vector3() });

  // Always keep isSpeakingRef in sync without tearing down the Three.js scene
  useEffect(() => {
    isSpeakingRef.current = isSpeaking;
  }, [isSpeaking]);

  // Map Azure Speech Visemes to morph targets (Layer 1)
  const updateVisemeTargets = useCallback((id, speaking) => {
    if (!speaking && id === 0) {
      targetMorphsRef.current = { mouthOpen: 0, mouthRound: 0, mouthWide: 0 };
      return;
    }

    switch (id) {
      case 0:
        targetMorphsRef.current = { mouthOpen: 0, mouthRound: 0, mouthWide: 0 };
        break;
      case 1:
      case 6:
      case 7:
      case 8:
        // E, I, soft vowel
        targetMorphsRef.current = { mouthOpen: 0.35, mouthRound: 0, mouthWide: 0.75 };
        break;
      case 2:
      case 9:
      case 11:
      case 12:
        // A, AH, open mouth
        targetMorphsRef.current = { mouthOpen: 0.85, mouthRound: 0.1, mouthWide: 0.5 };
        break;
      case 3:
      case 4:
      case 15:
      case 16:
        // O, U, W round mouth
        targetMorphsRef.current = { mouthOpen: 0.45, mouthRound: 0.9, mouthWide: 0 };
        break;
      case 18:
      case 20:
      case 21:
        // M, B, P closed lip
        targetMorphsRef.current = { mouthOpen: 0.1, mouthRound: 0.1, mouthWide: 0.3 };
        break;
      default:
        // General speaking fallback
        targetMorphsRef.current = { mouthOpen: 0.6, mouthRound: 0.2, mouthWide: 0.4 };
        break;
    }
  }, []);

  // Sync viseme changes to targets
  useEffect(() => {
    updateVisemeTargets(visemeId, isSpeaking);
  }, [visemeId, isSpeaking, updateVisemeTargets]);

  // Trigger wave animation with smooth crossfading
  useEffect(() => {
    if (isWaving && actionsRef.current['Friendly_Wave']) {
      const waveAction = actionsRef.current['Friendly_Wave'];
      const idleAction = actionsRef.current['Gentle_Idle'];

      if (idleAction) idleAction.fadeOut(0.25);
      waveAction.reset().setLoop(THREE.LoopOnce).fadeIn(0.25).play();

      const onFinished = (e) => {
        if (e.action === waveAction) {
          if (mixerRef.current) {
            mixerRef.current.removeEventListener('finished', onFinished);
          }
          waveAction.fadeOut(0.25);
          if (idleAction) idleAction.reset().fadeIn(0.25).play();
        }
      };

      if (mixerRef.current) {
        mixerRef.current.addEventListener('finished', onFinished);
      }
    }
  }, [isWaving]);

  // Function to adjust camera framing based on container size and model bounding box
  const fitCameraToModel = useCallback((camera, containerW, containerH) => {
    if (!modelRef.current || !camera) return;

    const { size } = modelBoundsRef.current;
    if (size.y === 0) return;

    const aspect = containerW && containerH ? containerW / containerH : 1.0;
    camera.aspect = aspect;

    const fovRad = (camera.fov * Math.PI) / 180;
    let distVert = (size.y / 2) / Math.tan(fovRad / 2);
    const horizFovRad = 2 * Math.atan(Math.tan(fovRad / 2) * aspect);
    let distHoriz = (size.x / 2) / Math.tan(horizFovRad / 2);

    let dist = Math.max(distVert, distHoriz);
    dist *= 1.15; // 15% margin padding for dead-center framing

    camera.position.set(0, 0, dist);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
  }, []);

  // Main Three.js Scene Setup — Runs EXACTLY ONCE on mount
  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    const containerWidth = container.clientWidth || 180;
    const containerHeight = container.clientHeight || 180;

    // 1. Scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // 2. Camera
    const camera = new THREE.PerspectiveCamera(32, containerWidth / containerHeight, 0.1, 100);
    cameraRef.current = camera;

    // 3. Renderer
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(containerWidth, containerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    renderer.domElement.style.display = 'block';

    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // 4. Studio Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.6);
    scene.add(ambientLight);

    const mainLight = new THREE.DirectionalLight(0xfffaed, 2.2);
    mainLight.position.set(2, 4, 3.5);
    mainLight.castShadow = true;
    scene.add(mainLight);

    const fillLight = new THREE.DirectionalLight(0xcdedf6, 1.2);
    fillLight.position.set(-3, 2, 2);
    scene.add(fillLight);

    const topLight = new THREE.DirectionalLight(0xffffff, 0.9);
    topLight.position.set(0, 5, 0);
    scene.add(topLight);

    // 5. Load GLB Model & Pivot Group Centering
    const loader = new GLTFLoader();
    loader.load(
      '/assets/avatar/CareBridge.glb',
      (gltf) => {
        const model = gltf.scene;
        modelRef.current = model;

        // Wrap inside a parent pivot group at origin (0, 0, 0)
        const pivot = new THREE.Group();
        pivot.add(model);
        scene.add(pivot);

        // Compute exact bounding box of model after matrix update
        model.updateMatrixWorld(true);
        const box = new THREE.Box3().setFromObject(model);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        modelBoundsRef.current = { size, center };

        // Center inner model on geometric centroid (-center.x, -center.y, -center.z)
        model.position.set(-center.x, -center.y, -center.z);

        // Auto-fit camera position looking at pivot origin (0, 0, 0)
        fitCameraToModel(camera, containerWidth, containerHeight);

        // Find Mouth morph target mesh
        model.traverse((child) => {
          if (child.isMesh) {
            child.castShadow = true;
            child.receiveShadow = true;
            if (child.morphTargetDictionary && (
              child.morphTargetDictionary['mouthOpen'] !== undefined ||
              child.morphTargetDictionary['mouthRound'] !== undefined ||
              child.morphTargetDictionary['mouthWide'] !== undefined ||
              child.name.toLowerCase().includes('mouth')
            )) {
              mouthMeshRef.current = child;
            }
          }
        });

        // 6. Setup Animation Mixer
        if (gltf.animations && gltf.animations.length > 0) {
          const mixer = new THREE.AnimationMixer(model);
          mixerRef.current = mixer;

          gltf.animations.forEach((clip) => {
            const action = mixer.clipAction(clip);
            actionsRef.current[clip.name] = action;
          });

          // Play Gentle_Idle by default (wave animation triggered cleanly via crossfade)
          if (actionsRef.current['Gentle_Idle']) {
            actionsRef.current['Gentle_Idle'].play();
          }
        }

        setLoading(false);
      },
      undefined,
      (err) => {
        console.error('Failed to load CareBridge 3D avatar:', err);
        setError('Failed to load 3D model');
        setLoading(false);
      }
    );

    // 7. Automatic Blink Loop
    const blinkInterval = setInterval(() => {
      if (actionsRef.current['Eye_L_CTRL_Blink'] && actionsRef.current['Eye_R_CTRL_Blink']) {
        const leftBlink = actionsRef.current['Eye_L_CTRL_Blink'];
        const rightBlink = actionsRef.current['Eye_R_CTRL_Blink'];
        leftBlink.reset().setLoop(THREE.LoopOnce).play();
        rightBlink.reset().setLoop(THREE.LoopOnce).play();
      }
    }, 4200);

    // 8. 60 FPS Render Loop (AnimationMixer + Lip Sync Engine)
    const clock = new THREE.Clock();
    const animate = () => {
      animationFrameIdRef.current = requestAnimationFrame(animate);
      const delta = clock.getDelta();
      const elapsedTime = clock.getElapsedTime();

      // Update Three.js animation mixer
      if (mixerRef.current) {
        mixerRef.current.update(delta);
      }

      // Calculate Target Morph Influences (Layer 1 + Layer 2)
      let targetOpen = targetMorphsRef.current.mouthOpen;
      let targetRound = targetMorphsRef.current.mouthRound;
      let targetWide = targetMorphsRef.current.mouthWide;

      if (isSpeakingRef.current) {
        // Layer 2 Fallback Oscillation: Multi-frequency sine wave
        const speechWave = Math.abs(Math.sin(elapsedTime * 14) * Math.cos(elapsedTime * 7));
        const wideWave = Math.abs(Math.sin(elapsedTime * 9));

        targetOpen = Math.max(targetOpen, 0.2 + speechWave * 0.65);
        targetWide = Math.max(targetWide, 0.25 + wideWave * 0.45);
      } else {
        targetOpen = 0;
        targetRound = 0;
        targetWide = 0;
      }

      // Apply to Morph Mesh with Smooth Lerp Interpolation
      if (mouthMeshRef.current && mouthMeshRef.current.morphTargetDictionary) {
        const dict = mouthMeshRef.current.morphTargetDictionary;
        const influences = mouthMeshRef.current.morphTargetInfluences;
        const lerpFactor = 0.22;

        const targets = {
          mouthOpen: targetOpen,
          mouthRound: targetRound,
          mouthWide: targetWide,
        };

        ['mouthOpen', 'mouthRound', 'mouthWide'].forEach((key) => {
          if (dict[key] !== undefined) {
            const targetVal = targets[key] || 0;
            currentMorphsRef.current[key] += (targetVal - currentMorphsRef.current[key]) * lerpFactor;
            influences[dict[key]] = currentMorphsRef.current[key];
          }
        });
      }

      renderer.render(scene, camera);
    };
    animate();

    // 9. Window Resize Handler
    const handleResize = () => {
      if (!container || !rendererRef.current || !cameraRef.current) return;
      const w = container.clientWidth || 180;
      const h = container.clientHeight || 180;
      rendererRef.current.setSize(w, h);
      fitCameraToModel(cameraRef.current, w, h);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      clearInterval(blinkInterval);
      window.removeEventListener('resize', handleResize);
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
      if (rendererRef.current && rendererRef.current.domElement) {
        rendererRef.current.domElement.remove();
        rendererRef.current.dispose();
      }
    };
  }, [fitCameraToModel]);

  return (
    <div
      className={`carebridge-avatar-container ${className}`}
      style={{
        width,
        height,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        borderRadius: '50%',
        background: 'transparent',
      }}
    >
      {loading && (
        <div style={{ color: '#5d8552', fontWeight: '600', fontSize: '0.85rem', textAlign: 'center' }}>
          ✨ CareBridge 3D...
        </div>
      )}

      {error && (
        <div style={{ color: '#c94a4a', fontSize: '0.85rem', textAlign: 'center', padding: '10px' }}>
          🤖 CareBridge
        </div>
      )}

      <div ref={mountRef} style={{ width: '100%', height: '100%', pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
    </div>
  );
};

export default CareBridgeAvatar;
